import type { AxiosInstance } from 'axios';
import { ResponseData } from '../types/axios';
import { Item } from '../types/item';
import { EJobStatus, Job } from '../types/job';

const POLL_INTERVAL_MS = 1000;
const POLL_MAX_ATTEMPTS = 60;

interface DeleteItemArgs {
  tenantId: string;
  item: Item;
  /** Also soft-delete the item's jobs+tasks (only applies to the unregistered path). */
  withJobs?: boolean;
}

/**
 * Delete a single item. An unregistered item is soft-deleted directly. A registered item must first
 * be de-registered from the identification worker: create a `deleteRegistration` job and poll it
 * until it leaves RUNNING (the worker callback then marks the item deleted + unlocks its SKU when
 * empty). Resolves once the item is fully gone; throws on a timeout or a failed request.
 *
 * This is the shared core of the single-item delete UI (`items-actions.tsx`) and the SKU
 * cascade delete (`use-delete-sku`).
 */
export const deleteItem = async (
  axios: AxiosInstance,
  { tenantId, item, withJobs = true }: DeleteItemArgs,
): Promise<void> => {
  // Unregistered items can be deleted normally.
  if (!item.isRegistered) {
    await axios.delete(`/items/${item.id}`, { params: { withJobs } });
    return;
  }

  // Registered items go through the identification worker de-registration.
  const response = await axios.post<ResponseData<Job>>('/jobs/identification/deleteRegistration', {
    tenantId,
    itemId: item.id,
  });
  if (response.status !== 200) {
    throw new Error(response.statusText);
  }

  const getJobUrl = `/jobs/${response.data.data.id}`;
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    const jobResponse = await axios.get<ResponseData<Job>>(getJobUrl);
    if (jobResponse?.data?.data?.status !== EJobStatus.RUNNING) {
      return;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  throw new Error('Timeout while waiting for deleting to finish');
};
